import { useEffect, useState } from "react";
import {
  BooleanInput,
  ReferenceInput,
  AutocompleteInput,
  required,
  TextInput,
  SelectInput,
  NumberInput,
  FormDataConsumer,
  minValue,
} from "react-admin";
import { useFormContext, useWatch } from "react-hook-form";
import { Typography, TextField, Grid, FormControlLabel, Switch } from "@mui/material";
import { getFile } from "server/components/DownloadFile";
import CustomDateInput from "server/components/Fields/CustomDateInput";
import { CourseChoices } from "utils/choices";
import {
  AdmissionReason,
  FreeEnrolingPaymentMethod,
  NotOnlinePaidMethods,
  PmsAdmissionReason,
  PmsTypes,
  ReceptionType,
} from "utils/constants";
import { studentRequests } from "api/studentApi";
import Education from "./EducationSection";
import dataProvider from "server/dataProvider";
import { settings } from "app";
import PrivateCustomImageInput from "./PrivateCustomImageInput";
import { administrationRequests } from "api/administrationApi";
import { getUniversityName, tenants } from "utils/tenants";
import { useNotifyError } from "utils/useNotifyError";

// PMS103 lets the user pick between these two admission reasons manually, instead of the value being auto-set
const PMS103_ADMISSION_REASON_IDS = [
  PmsAdmissionReason.Pms103FAdmissionReasonId,
  PmsAdmissionReason.Pms103S26AdmissionReasonId,
];

const AcademicInformationSection = ({
  isFreeEnrolling,
  userData,
  isDisable,
  setIsDisable,
  defaultBirthCountry,
  setProfilePictureDocumentId,
  setDiplomaIds,
  setLanguageId,
  notify,
  specialityId,
  setSpecialityId,
  curriculumId = null,
  setCurriculumId,
  isKSK = false,
  isOther = false,
  isPms = false,
  isIndividual = false,
  isForeigner,
  providedSpecialGroup,
  isNotPaidOnline,
  availableGrades,
  languageId,
  pmsType,
}) => {
  const notifyError = useNotifyError();
  const [studentsCount, setStudentsCount] = useState(null);
  const [isSportActive, setIsSportActive] = useState(false);
  const isMgu = settings.tenant === tenants.MGU;
  const diplomaDocumentIds = userData?.diploma?.documentIds;
  const hasDiplomaDocumentIds = diplomaDocumentIds?.length > 0;
  const profilePictureDocumentId = userData?.profilePictureDocumentId;

  const shouldShowPaymentMethod =
    isFreeEnrolling || (!isKSK && !isPms) || (isKSK && isNotPaidOnline) || (isPms && pmsType !== PmsTypes.PMS103);

  const paymentMethodChoices = isFreeEnrolling
    ? FreeEnrolingPaymentMethod
    : shouldShowPaymentMethod
      ? NotOnlinePaidMethods
      : null;

  const { setValue } = useFormContext();

  useEffect(() => {
    if (!userData?.diploma?.issuerSettlementId) return;
    dataProvider
      .customRequest(administrationRequests.getSettlementById(userData?.diploma?.issuerSettlementId), null, "GET")
      .then(settlement => {
        setValue("diploma.issuerSettlementId", settlement?.data?.id);
      })
      .catch(notifyError);
  }, [userData?.diploma?.issuerSettlementId, setValue, notifyError]);

  useEffect(() => {
    if (!isPms) return;

    if (pmsType === PmsTypes.PMS228) {
      setValue("admissionReasonId", PmsAdmissionReason.Pms228AdmissionReasonId);
    } else if (pmsType === PmsTypes.PMS103) {
      // clear a value left over from PMS228 — it won't be among the PMS103-filtered choices
      setValue("admissionReasonId", null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pmsType]);

  const course = useWatch({ name: "otherUniversityInfo.course" });
  const studyGroup = useWatch({ name: "studyGroup" });
  const studyTypeId = useWatch({ name: "studyTypeId" });
  const admissionAfterEducationLevelId = useWatch({ name: "admissionAfterEducationLevelId" });

  // The curriculum is no longer valid once the speciality changes — reset it until
  // the current one for the new speciality reloads (see the effect in CreateTemplate.js).
  useEffect(() => {
    setCurriculumId(null);
    setValue("curriculumId", null);
  }, [specialityId, setCurriculumId, setValue]);

  const shouldShowStudentsCount = isOther ? studyGroup && course && specialityId : studyGroup && specialityId;

  useEffect(() => {
    const courseValue = isOther ? course : 1;
    const shouldFetch = specialityId && studyGroup && courseValue;
    if (shouldFetch) {
      const fetchCount = async () => {
        try {
          const { data } = await dataProvider.customRequest(
            studentRequests.getStudentsCount({
              startingYearOnly: true,
              specialityId,
              course: courseValue,
              studyGroup,
            }),
            null,
            "GET",
          );
          setStudentsCount(data.studentsCount);
        } catch (e) {
          notifyError(e);
        }
      };
      fetchCount();
    }
  }, [specialityId, course, studyGroup, setStudentsCount, notifyError, isOther]);
  return (
    <Grid
      container
      spacing={2}
    >
      <Grid
        item
        xs={12}
      >
        <Typography
          color={"primary"}
          fontSize={18}
          py={2}
        >
          Данни за диплома от предходно образование
        </Typography>
      </Grid>
      <Grid
        item
        xs={12}
        md={4}
      >
        <TextInput
          defaultValue={userData?.diploma?.series}
          source="diploma.series"
          label="Серия"
          validate={!isForeigner && !isMgu ? required("Задължително") : null}
          readOnly={isDisable}
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={4}
      >
        <TextInput
          defaultValue={userData?.diploma?.number}
          source="diploma.number"
          label="Номер"
          validate={!isForeigner && !isMgu ? required("Задължително") : null}
          readOnly={isDisable}
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={4}
      >
        <TextInput
          defaultValue={userData?.diploma?.registrationNumber}
          source="diploma.registrationNumber"
          label="Рег. номер"
          validate={!isForeigner && !isMgu ? required("Задължително") : null}
          readOnly={isDisable}
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={4}
      >
        <TextInput
          source="diploma.issuedBy"
          label="Издадена от:"
          validate={!isMgu && !isForeigner ? required("Задължително") : null}
          defaultValue={userData?.diploma?.issuedBy}
          readOnly={isDisable}
        />
      </Grid>

      <Grid
        item
        md={4}
      >
        <CustomDateInput
          source="diploma.issuedOn"
          label="Издадена на:"
          defaultValue={userData?.diploma?.issuedOn}
          validate={!isMgu && !isForeigner ? required("Задължително") : null}
          readOnly={isDisable}
          required={!isMgu && !isForeigner}
        />
      </Grid>
      <Grid
        item
        md={4}
      >
        <TextInput
          source="diploma.graduatedOn"
          label="Година  на завършване:"
          defaultValue={userData?.diploma?.graduatedOn !== undefined ? userData?.diploma?.graduatedOn : null}
          validate={!isMgu && !isForeigner ? required("Задължително") : null}
          readOnly={isDisable}
        />
      </Grid>
      <FormDataConsumer>
        {({ formData }) => (
          <>
            <Grid
              item
              xs={12}
              md={4}
            >
              <ReferenceInput
                reference={administrationRequests.getCountriesSelect()}
                source="diploma.issuerCountryId"
              >
                <AutocompleteInput
                  defaultValue={!isForeigner ? defaultBirthCountry : null}
                  filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
                  optionText="nameBg"
                  label="Държава"
                  validate={!isMgu && !isForeigner ? required("Задължително") : null}
                  readOnly={isDisable}
                  fullWidth
                />
              </ReferenceInput>
            </Grid>
            {formData?.diploma?.issuerCountryId === defaultBirthCountry ? (
              <Grid
                item
                xs={12}
                md={4}
              >
                <ReferenceInput
                  reference={administrationRequests.getSettlementsSelect()}
                  source="diploma.issuerSettlementId"
                  filter={{ showDistrictAndMunicipality: true, showPostalCode: true }}
                >
                  <AutocompleteInput
                    filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
                    optionText="nameBg"
                    optionValue="id"
                    label="Населено място"
                    validate={!isMgu && !isForeigner ? required("Задължително") : null}
                    readOnly={isDisable}
                    fullWidth
                  />
                </ReferenceInput>
              </Grid>
            ) : (
              <Grid
                item
                xs={12}
                lg={4}
              >
                <TextInput
                  label="Населено място"
                  source="diploma.issuerSettlement"
                  validate={!isMgu && !isForeigner ? required("Задължително") : null}
                />
              </Grid>
            )}
          </>
        )}
      </FormDataConsumer>
      <Grid
        item
        xs={12}
        md={4}
      >
        <TextInput
          source="diploma.academicProfile"
          label="Профил"
          defaultValue={isForeigner ? "Общообразователен" : userData?.diploma?.academicProfile}
          validate={!isMgu && !isForeigner ? required("Задължително") : null}
          readOnly={isDisable || isForeigner}
        />
      </Grid>
      {!isForeigner &&
        isKSK &&
        availableGrades?.map((exam, idx) => {
          return (
            <Grid
              item
              xs={12}
              md={4}
              key={idx}
            >
              <TextField
                label={`${exam.nameBg} `}
                disabled
                value={exam.grade}
                type="number"
              />
            </Grid>
          );
        })}
      {isForeigner && (
        <>
          <Grid
            item
            xs={12}
          >
            {hasDiplomaDocumentIds && (
              <>
                <Typography
                  color={"primary"}
                  fontSize={18}
                  py={2}
                >
                  Прикачени снимки за удостоверение на средно образование
                </Typography>
                {diplomaDocumentIds.map(docId => (
                  <Typography
                    key={docId}
                    sx={{ textDecoration: "underline", cursor: "pointer" }}
                    fontSize={16}
                    fontWeight={"bold"}
                    mb={3}
                    onClick={() => getFile(docId, notify)}
                  >{`image-${docId}.jpeg`}</Typography>
                ))}
              </>
            )}
          </Grid>
          <Grid
            item
            xs={12}
          >
            <PrivateCustomImageInput
              source="documentIds"
              name="documentIds"
              isDisable={isDisable}
              setIsDisable={setIsDisable}
              label={`${hasDiplomaDocumentIds ? "Смени снимки за удостоверение на средно образование" : "Прикачи снимки за удостоверение на средно образование"} `}
              SubCategoryId={10}
              setDataId={setDiplomaIds}
              isMulty={true}
            />
          </Grid>
        </>
      )}
      <Grid
        item
        xs={12}
      >
        <>
          {isOther && (
            <>
              <Typography
                color={"primary"}
                fontSize={18}
                paddingY={2}
              >
                Идва от
              </Typography>

              <Grid
                container
                spacing={2}
              >
                <Grid
                  item
                  xs={12}
                  md={4}
                >
                  <TextInput
                    source="otherUniversityInfo.universityName"
                    label="Име на Университет"
                    validate={!isForeigner ? required("Задължително") : null}
                    readOnly={isDisable}
                  />
                </Grid>
                <FormDataConsumer>
                  {({ formData }) => (
                    <>
                      <Grid
                        item
                        xs={12}
                        md={4}
                      >
                        <ReferenceInput
                          reference={administrationRequests.getCountriesSelect()}
                          source="otherUniversityInfo.countryId"
                        >
                          <AutocompleteInput
                            defaultValue={!isForeigner ? 21 : null}
                            filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
                            optionText="nameBg"
                            label="Държава"
                            validate={!isForeigner ? required("Задължително") : null}
                            readOnly={isDisable}
                            fullWidth
                          />
                        </ReferenceInput>
                      </Grid>
                      {formData?.otherUniversityInfo?.countryId === defaultBirthCountry ? (
                        <Grid
                          item
                          xs={12}
                          md={4}
                        >
                          <ReferenceInput
                            reference={administrationRequests.getSettlementsSelect()}
                            source="otherUniversityInfo.settlementId"
                            filter={{ showDistrictAndMunicipality: true, showPostalCode: true }}
                          >
                            <AutocompleteInput
                              filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
                              optionText="nameBg"
                              label="Населено място"
                              validate={
                                !isForeigner && formData?.otherUniversityInfo?.countryId === defaultBirthCountry
                                  ? required("Задължително")
                                  : null
                              }
                              readOnly={isDisable}
                              fullWidth
                            />
                          </ReferenceInput>
                        </Grid>
                      ) : (
                        <>
                          <Grid
                            item
                            xs={12}
                            lg={4}
                          >
                            <TextInput
                              label="Населено място"
                              source="otherUniversityInfo.settlement"
                              validate={
                                !isForeigner && formData?.otherUniversityInfo?.countryId !== defaultBirthCountry
                                  ? required("Задължително")
                                  : null
                              }
                            />
                          </Grid>
                        </>
                      )}
                    </>
                  )}
                </FormDataConsumer>
                <Grid
                  item
                  xs={12}
                  md={4}
                >
                  <SelectInput
                    label="Курс"
                    source="otherUniversityInfo.course"
                    choices={CourseChoices}
                    validate={!isForeigner ? required("Задължително") : null}
                    readOnly={isDisable}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </>
          )}
        </>
      </Grid>
      {/* //Прием */}
      <Grid
        item
        xs={12}
      >
        <Typography
          color={"primary"}
          fontSize={18}
          py={2}
        >
          Прием в {getUniversityName(settings.tenant)}
        </Typography>
      </Grid>
      <Grid
        item
        xs={12}
      >
        <Education
          specialityId={specialityId}
          setSpecialityId={setSpecialityId}
          isDisable={isDisable}
          userData={userData}
          isKSK={isKSK}
          isOther={isOther}
          isPms={isPms}
          isIndividual={isIndividual}
          isForeigner={isForeigner}
          setLanguageId={setLanguageId}
        />

        <Grid
          item
          xs={12}
          md={4}
        >
          <NumberInput
            onWheel={e => e.target.blur()}
            onKeyDown={e => {
              if (e.key.length === 1 && !/^\d$/.test(e.key)) {
                e.preventDefault();
              }
            }}
            label="Учебна група"
            source="studyGroup"
            validate={minValue(0, "Минималната стойност е 0")}
            readOnly={isDisable}
            fullWidth
          />
        </Grid>
        {shouldShowStudentsCount && (
          <Grid
            item
            xs={12}
            md={4}
            sx={{ display: "flex", alignItems: "center" }}
          >
            <Typography
              border={"1px solid green"}
              borderRadius={1}
              color={"green"}
              fontSize={18}
              py={1}
              px={2}
              marginBottom={2}
              width="100%"
            >
              Брой студенти в група: {studentsCount !== null || studentsCount !== undefined ? studentsCount : "..."}
            </Typography>
          </Grid>
        )}

        {specialityId && studyTypeId && admissionAfterEducationLevelId && isOther && (
          <Grid
            item
            xs={12}
            md={4}
          >
            <ReferenceInput
              key={specialityId}
              source="curriculumId"
              filter={{ specialityId, studyTypeId, admissionAfterEducationLevelId }}
              reference={studentRequests.getCurriculumsSelect()}
            >
              <SelectInput
                label="Учебен план"
                optionText="name"
                defaultValue={curriculumId}
                onChange={e => setCurriculumId(e?.target?.value ?? null)}
                validate={!isForeigner ? required("Задължително") : null}
                readOnly={isDisable}
                fullWidth
              />
            </ReferenceInput>
          </Grid>
        )}
      </Grid>
      <Grid
        item
        xs={12}
        md={4}
      >
        <ReferenceInput
          source="admissionReasonId"
          reference={administrationRequests.getAdmissionReasonsSelect()}
          filter={isPms && pmsType === PmsTypes.PMS103 ? { ids: PMS103_ADMISSION_REASON_IDS } : undefined}
        >
          {/* (isKSK): the admin picks the admission reason every time - it is not prefilled from
              the candidacy, and so it must not be locked by it either (the field is required). */}
          <AutocompleteInput
            defaultValue={
              isKSK
                ? undefined
                : (userData?.admissionReasonId ?? (isMgu ? AdmissionReason.WithoutCompetitiveExam : undefined))
            }
            optionText="name"
            label="Основание за прием"
            optionValue="id"
            validate={!isForeigner ? required("Задължително") : null}
            readOnly={
              isPms && pmsType === PmsTypes.PMS228 ? true : isDisable || (!isKSK && !!userData?.admissionReasonId)
            }
            fullWidth
          />
        </ReferenceInput>
      </Grid>
      <Grid
        item
        xs={12}
        md={4}
      >
        <ReferenceInput
          source="receptionTypeId"
          reference={administrationRequests.getReceptionTypesSelect()}
        >
          <AutocompleteInput
            defaultValue={
              !isForeigner
                ? (userData?.receptionTypeId ?? (isMgu ? ReceptionType.StateOrder : undefined))
                : ReceptionType.PaidOrder
            }
            optionText="name"
            optionValue="id"
            label="Тип прием"
            validate={!isForeigner ? required("Задължително") : null}
            readOnly={isDisable || userData?.receptionTypeId || isForeigner}
            fullWidth
          />
        </ReferenceInput>
      </Grid>
      <Grid
        item
        xs={12}
        md={4}
      >
        <ReferenceInput
          filter={{ VisibleForCandidates: true }}
          source="admissionAfterEducationLevelId"
          reference={administrationRequests.getAdmissionAfterEducationLevelsSelect()}
        >
          <AutocompleteInput
            optionText="name"
            label="Прием след"
            readOnly={isDisable}
            defaultValue={userData?.admissionAfterEducationLevelId || 1}
            fullWidth
          />
        </ReferenceInput>
      </Grid>
      {/* {specialityId && (
        <Grid
          item
          xs={12}
          md={4}
        >
          <ReferenceInput
            source="instructionLanguageId"
            reference={administrationRequests.getInstructionLanguagesBySpecialityId(specialityId)}
          >
            <AutocompleteInput
              optionText="nameBg"
              optionValue="id"
              label="Език на обучение"
              validate={required("Задължително")}
              defaultValue={userData?.instructionLanguageId}
              onChange={e => setLanguageId(e)}
              readOnly={isDisable || userData?.instructionLanguageId}
              fullWidth
            />
          </ReferenceInput>
        </Grid>
      )} */}

      {paymentMethodChoices && settings.tenant === tenants.MU && (
        <Grid
          item
          xs={12}
          md={4}
        >
          <SelectInput
            source="paymentMethod"
            label="Платена такса"
            choices={paymentMethodChoices}
            validate={required()}
          />
        </Grid>
      )}
      {(isKSK && userData?.provideSpecialGroup) ||
        (!isKSK && providedSpecialGroup && (
          <Grid
            item
            md={4}
          >
            <BooleanInput
              sx={{ mt: "1rem" }}
              source="inSpecialGroup"
              label="Добави специална група"
              readOnly={isDisable}
            />
          </Grid>
        ))}

      <Grid
        item
        xs={12}
      >
        <FormControlLabel
          label="Занимавал съм се професионално със спорт"
          control={
            <Switch
              checked={isSportActive}
              onChange={() => setIsSportActive(!isSportActive)}
            />
          }
        />
      </Grid>
      {isSportActive && (
        <Grid
          item
          xs={12}
          md={4}
        >
          <TextInput
            source="sportDescription"
            label="Спорт"
            validate={required()}
          />
        </Grid>
      )}
      {/* //Picture part */}
      <Grid
        item
        xs={12}
      >
        {profilePictureDocumentId != null && (
          <>
            <Typography
              color={"primary"}
              fontSize={18}
              py={2}
            >
              Прикачена профилна снимка
            </Typography>
            <Typography
              sx={{ textDecoration: "underline", cursor: "pointer" }}
              fontSize={16}
              fontWeight={"bold"}
              mb={3}
              onClick={() => getFile(profilePictureDocumentId, notify)}
              // onClick={() => getFile("865", notify)}
            >{`image-${profilePictureDocumentId}.jpeg`}</Typography>
          </>
        )}
      </Grid>
      <Grid
        item
        xs={12}
      >
        <PrivateCustomImageInput
          source="profilePictureDocumentId"
          name="profilePictureDocumentId"
          isDisable={isDisable}
          setIsDisable={setIsDisable}
          label={`${profilePictureDocumentId != null ? "Смени Профилна Снимка" : "Прикачи Профилна Снимка"}`}
          SubCategoryId={6}
          setDataId={setProfilePictureDocumentId}
        />
      </Grid>
    </Grid>
  );
};
export default AcademicInformationSection;
